
import { useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isEnglish = location.pathname.includes('/en');

  const handleScroll = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleLanguage = () => {
    if (isEnglish) {
      navigate('/');
    } else {
      navigate('/en');
    }
  };

  const menuItems = isEnglish ? [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About Us' },
    { id: 'stats', label: 'Achievements' },
    { id: 'products', label: 'Products' },
    { id: 'manufacturing', label: 'Manufacturing' },
    { id: 'features', label: 'Why Choose Us' },
    { id: 'quote', label: 'Get Quote' },
  ] : [
    { id: 'home', label: 'الرئيسية' },
    { id: 'about', label: 'من نحن' },
    { id: 'stats', label: 'إنجازاتنا' },
    { id: 'products', label: 'منتجاتنا' },
    { id: 'quality', label: 'الجودة' },
    { id: 'features', label: 'ما يميزنا' },
    { id: 'technology', label: 'التقنية' },
    { id: 'gallery', label: 'المعرض' },
    { id: 'blog', label: 'الأخبار' },
    { id: 'quote', label: 'طلب عرض سعر' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100/50 shadow-sm">
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <img 
              src="/lovable-uploads/0918c080-6da6-4122-9a21-95a98e0fa2e6.png" 
              alt={isEnglish ? "Dan Plastic - Logo" : "شركة دان - لوجو"} 
              className="h-12 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center justify-center flex-1">
            <div className={`flex items-center gap-2 ${isEnglish ? 'flex-row' : 'flex-row-reverse'}`}>
              {menuItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => handleScroll(item.id)}
                  className={`
                    relative px-5 py-3 text-sm font-bold text-gray-800 
                    hover:text-blue-600 transition-all duration-300 
                    whitespace-nowrap group rounded-lg hover:bg-blue-50/80
                    ${isEnglish ? 'text-left' : 'text-right'}
                    border border-transparent hover:border-blue-200/50
                  `}
                >
                  {item.label}
                  <span className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-4/5 rounded-full"></span>
                </button>
              ))}
            </div>
          </nav>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-800 hover:text-blue-600 
                       border border-gray-200 hover:border-blue-300 rounded-lg transition-all duration-300 
                       bg-gray-50/80 hover:bg-blue-50/80 backdrop-blur-sm shadow-sm hover:shadow-md"
              title={isEnglish ? "Switch to Arabic" : "التبديل إلى الإنجليزية"}
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">
                {isEnglish ? 'عربي' : 'EN'}
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-3 text-gray-800 hover:text-blue-600 hover:bg-blue-50/80 rounded-lg transition-all duration-300 border border-gray-200 hover:border-blue-300"
              aria-label={isEnglish ? "Toggle menu" : "فتح القائمة"}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`
          lg:hidden overflow-hidden transition-all duration-500 ease-in-out
          ${isMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="py-6 border-t border-gray-100/70 bg-white/98 backdrop-blur-sm rounded-b-lg shadow-lg">
            <div className="space-y-2">
              {menuItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => handleScroll(item.id)}
                  className={`
                    block w-full py-4 px-6 text-gray-800 hover:text-blue-600 
                    hover:bg-blue-50/80 transition-all duration-300 rounded-lg 
                    font-bold border border-transparent hover:border-blue-200/50
                    ${isEnglish ? 'text-left' : 'text-right'}
                  `}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
